import React, { useEffect, useState } from "react";
import axios from "axios";
import { Zap } from "lucide-react";
import { API } from "../../config/api";

// Recent-sales ticker bar — sits at the top of /marketplace and shows the
// last few completed sales in a horizontally scrolling marquee. Drives
// social proof: when buyers see other deals closing, conversion lifts.
//
// Anonymity rule (mirrors the marketplace backend): we surface ONLY the
// channel handle, price, and relative timestamp. Never buyer or seller.
//
// Hides itself when there are no qualifying sales (cold market) — better
// to be invisible than to flash "no sales yet" social proof in reverse.
const RecentSalesTicker = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/marketplace/recent-sales?limit=8`);
        if (!cancelled) setItems(data.items || []);
      } catch (_) { /* silent — ticker is non-critical */ }
    };
    load();
    // Refresh every 30s so a fresh sale appears within a half-minute
    // across all open tabs. Cheap query, no auth needed.
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!items || items.length === 0) return null;

  const relative = (iso) => {
    try {
      const ago = (Date.now() - new Date(iso).getTime()) / 1000;
      if (ago < 60) return `${Math.max(1, Math.floor(ago))}s ago`;
      if (ago < 3600) return `${Math.floor(ago / 60)}m ago`;
      if (ago < 86400) return `${Math.floor(ago / 3600)}h ago`;
      return `${Math.floor(ago / 86400)}d ago`;
    } catch (_) { return ""; }
  };

  // Double the list so the marquee loops seamlessly without a visible reset.
  const ribbon = [...items, ...items];

  return (
    <div
      className="bg-gradient-to-r from-emerald-500/10 via-amber-500/10 to-emerald-500/10 border-y border-emerald-500/30 overflow-hidden mb-5 -mx-4 sm:mx-0 sm:rounded-lg"
      data-testid="recent-sales-ticker"
    >
      <div className="flex items-center gap-3 py-2.5 px-4">
        <span className="shrink-0 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-300 font-semibold whitespace-nowrap">
          <Zap className="h-3.5 w-3.5 animate-pulse" /> Just sold
        </span>
        <div className="flex-1 overflow-hidden">
          <div className="flex gap-6 animate-marquee whitespace-nowrap">
            {ribbon.map((s, i) => (
              <span
                key={`${s.channel_username}-${i}`}
                className="text-sm text-gray-200 shrink-0"
                data-testid={`ticker-item-${i}`}
              >
                <span className="font-mono text-emerald-300">/{s.channel_username}</span>
                <span className="text-gray-500"> sold for </span>
                <span className="font-bold text-amber-200">{s.price_credits.toLocaleString()} cr</span>
                <span className="text-gray-500"> · {relative(s.sold_at)}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentSalesTicker;
